import { z } from 'zod';
import { devicePlatformSchema, updateChannelSchema } from './device.js';
import { pluginDescriptorSchema } from './plugin.js';
/**
 * Server-driven конфигурация клиентов (PLAYBACK_TRANSCODING.md,
 * «Административное управление»).
 *
 * Правила:
 * - конфигурация версионируется и имеет состояния draft/published;
 * - секреты, внутренние URL и server-only параметры в client config не попадают;
 * - клиент получает platform-aware срез и не требует пересборки приложения.
 */
export const settingsScopeSchema = z.enum(['ui', 'playback', 'updates']);
const featureFlagsSchema = z.object({
    search: z.boolean().default(true),
    favorites: z.boolean().default(true),
    watchProgress: z.boolean().default(true),
    plugins: z.boolean().default(true),
    library: z.boolean().default(true),
});
const uiPlatformOverrideSchema = z.object({
    /** Порядок и видимость секций главной страницы. */
    hiddenSections: z.array(z.string().max(64)).max(32).default([]),
    /** Количество карточек в ряду; TV и браузер оправданно различаются. */
    railItemLimit: z.number().int().min(4).max(40).default(20),
    showRatings: z.boolean().default(true),
});
/**
 * Порядок источников в верхнем меню карточки: слева направо, как их увидит
 * зритель. Записан он КЛЮЧАМИ источников (`skaz`, `skaz:filmixtv`,
 * `rutracker-org`), а не тем, что видно в меню: подпись Skaz отдаёт вместе с
 * качеством этого тайтла и временами переименовывает витрину, и настройка,
 * записанная подписью, молча переставала работать.
 *
 * Список неполный по устройству: трекеры Jackett заводит администратор, а
 * витрины у сказа появляются и исчезают. Всё, чего в списке нет, идёт ПОСЛЕ
 * перечисленного, сохраняя свой обычный порядок, — иначе новый источник молча
 * прятался бы в конец или, того хуже, поднимался наверх.
 */
const sourceOrderSchema = z.object({
    online: z.array(z.string().min(1).max(96)).max(128).default([]),
    torrent: z.array(z.string().min(1).max(96)).max(128).default([]),
});
/**
 * Выключенные источники — теми же ключами, что и порядок.
 *
 * Выключенная группа уносит и всё, что внутри: «Skaz выключен» не может значить
 * «кроме витрины Rezka». Выключенная витрина убирает только себя, и это главный
 * случай: у сказа полтора десятка витрин, часть из них отдаёт рекламу вместо
 * фильма или требует релея, которого у нас нет.
 */
const sourceHiddenSchema = z.object({
    online: z.array(z.string().min(1).max(96)).max(128).default([]),
    torrent: z.array(z.string().min(1).max(96)).max(128).default([]),
});
export const uiSettingsSchema = z.object({
    brandName: z.string().min(1).max(60).default('Lumos'),
    /**
     * Прятать взрослое из лент и списков каталога.
     *
     * По умолчанию прячем: установка домашняя, и «Популярные фильмы» между
     * мультфильмом и семейной комедией показывали софткор, которого никто не
     * спрашивал. Отдельным зрителям это переопределяется
     * ({@link userSettingsOverrideSchema}) — решение принимает администратор, а
     * не тот, кто смотрит.
     */
    hideAdult: z.boolean().default(true),
    welcomeMessage: z.string().max(240).default(''),
    featureFlags: featureFlagsSchema.prefault({}),
    sourceOrder: sourceOrderSchema.prefault({}),
    sourceHidden: sourceHiddenSchema.prefault({}),
    platformOverrides: z
        .object({
        web: uiPlatformOverrideSchema.prefault({}),
        'android-tv': uiPlatformOverrideSchema.prefault({}),
    })
        .prefault({}),
});
export const playbackModeSchema = z.enum([
    'direct-stream',
    'remux',
    'audio-remux',
    'audio-transcode',
    'video-transcode',
    // Оба потока перекодируются: клиент не играет ни видеокодек, ни звук.
    'video-audio-transcode',
    'subtitle-convert',
    'subtitle-burn-in',
]);
export const playbackSettingsSchema = z.object({
    /** Порядок важен: сервер выбирает первый подходящий режим из списка. */
    allowedModes: z
        .array(playbackModeSchema)
        .min(1)
        .default([
        'direct-stream',
        'remux',
        'audio-remux',
        'audio-transcode',
        'video-transcode',
        'video-audio-transcode',
    ]),
    maxHeight: z.number().int().min(240).max(4320).default(1080),
    maxFrameRate: z.number().int().min(24).max(120).default(60),
    preferredVideoCodecs: z.array(z.string().max(24)).max(12).default(['h264', 'hevc', 'vp9', 'av1']),
    preferredAudioCodecs: z.array(z.string().max(24)).max(12).default(['aac', 'opus', 'mp3', 'ac3']),
    /** Ступени битрейта в кбит/с для будущего adaptive ladder. */
    bitrateLadderKbps: z
        .array(z.number().int().min(200).max(100_000))
        .max(10)
        .default([1500, 3000, 6000]),
    subtitlePolicy: z.enum(['prefer-text', 'convert', 'burn-in-if-needed']).default('convert'),
    hardwareAcceleration: z.enum(['auto', 'vaapi', 'cpu']).default('auto'),
    /** Ограниченный CPU fallback: очередь и число задач всегда ограничены. */
    maxConcurrentTranscodes: z.number().int().min(0).max(16).default(1),
    bufferingTargetMs: z.number().int().min(1000).max(120_000).default(20_000),
    playbackSessionTtlSeconds: z.number().int().min(60).max(86_400).default(21_600),
    retry: z
        .object({
        maxAttempts: z.number().int().min(0).max(10).default(3),
        backoffMs: z.number().int().min(100).max(60_000).default(1500),
    })
        .prefault({}),
});
export const updatesSettingsSchema = z.object({
    defaultChannel: updateChannelSchema.default('stable'),
    /** Разрешено ли клиенту скачивать APK, помеченный обязательным. */
    mandatoryUpdatesEnabled: z.boolean().default(true),
    rolloutPercent: z.number().int().min(0).max(100).default(100),
    /** Запрет отката версии по умолчанию (ANDROID_TV_UPDATES.md). */
    allowDowngrade: z.boolean().default(false),
});
export const settingsValueSchema = z.discriminatedUnion('scope', [
    z.object({ scope: z.literal('ui'), value: uiSettingsSchema }),
    z.object({ scope: z.literal('playback'), value: playbackSettingsSchema }),
    z.object({ scope: z.literal('updates'), value: updatesSettingsSchema }),
]);
export const settingsDocumentSchema = z.object({
    scope: settingsScopeSchema,
    /** Версия опубликованной конфигурации; растёт при каждой публикации. */
    version: z.number().int().nonnegative(),
    updatedAt: z.string(),
    updatedBy: z.string().nullable(),
    hasDraft: z.boolean(),
    published: z.record(z.string(), z.unknown()),
    draft: z.record(z.string(), z.unknown()).nullable(),
    /** История для rollback: последние опубликованные версии. */
    history: z.array(z.object({
        version: z.number().int().nonnegative(),
        publishedAt: z.string(),
        publishedBy: z.string().nullable(),
    })),
});
export const settingsListResponseSchema = z.object({
    scopes: z.array(settingsDocumentSchema),
});
/**
 * Индивидуальные настройки одного зрителя. Задаёт их администратор в админке:
 * своего экрана настроек у пользователя нет — иначе одно и то же решение
 * («каким источником смотреть», «какое качество отдавать») принималось бы в
 * двух местах, и владелец установки не знал бы, что у зрителя на самом деле.
 *
 * Здесь ТОЛЬКО переопределения: отсутствующее поле означает «как у всех», а не
 * «выключено». Поэтому все поля необязательные, а пустой документ — это
 * «никаких отличий от общих настроек».
 *
 * Полей немного, и это не заготовка «на вырост»: индивидуальным может быть
 * лишь то, что сервер применяет по зрителю. Ускорение, число одновременных
 * перекодировок и TTL сессии — свойства машины, а не человека, и остаются
 * общими для установки.
 */
export const userSettingsOverrideSchema = z.object({
    /** Свой порядок источников; отсутствующий список — как у всех. */
    sourceOrder: z
        .object({
        online: z.array(z.string().min(1).max(96)).max(128).optional(),
        torrent: z.array(z.string().min(1).max(96)).max(128).optional(),
    })
        .optional(),
    /** Свои выключенные источники; отсутствующий список — как у всех. */
    sourceHidden: z
        .object({
        online: z.array(z.string().min(1).max(96)).max(128).optional(),
        torrent: z.array(z.string().min(1).max(96)).max(128).optional(),
    })
        .optional(),
    /** Свой потолок качества: выше него зрителю не отдаём. */
    maxHeight: z.number().int().min(240).max(4320).optional(),
    /**
     * Прятать ли взрослое ЭТОМУ зрителю. Отсутствует — как у всех.
     *
     * Именно поэтому настройка индивидуальная, а не общая: в одном доме взрослый
     * и ребёнок смотрят с одного сервера, и «спрятать у всех» — не тот ответ,
     * которого от него ждут.
     */
    hideAdult: z.boolean().optional(),
    /**
     * Кому этот зритель соответствует в Jellyfin — идентификатор пользователя
     * медиатеки.
     *
     * Нужен для обмена прогрессом: Jellyfin хранит «где остановился» отдельно у
     * КАЖДОГО своего пользователя, и без явной привязки непонятно, чей просмотр
     * читать и чей писать. Сопоставлять по имени нельзя: в Lumos зритель может
     * называться иначе, чем в медиатеке, а совпадение имён у разных людей — не
     * повод считать их одним человеком.
     *
     * Отсутствует — обмена нет, и это рабочее состояние по умолчанию: медиатека
     * есть не у всех, а у кого есть — не всякий хочет делиться историей.
     */
    jellyfinUserId: z.string().min(1).max(64).optional(),
});
/** Пользователь и его отличия от общих настроек — строка списка в админке. */
export const userSettingsEntrySchema = z.object({
    userId: z.string(),
    username: z.string(),
    displayName: z.string(),
    role: z.enum(['admin', 'user']),
    override: userSettingsOverrideSchema,
    updatedAt: z.string().nullable(),
    updatedBy: z.string().nullable(),
});
/** Пользователь медиатеки: к нему привязывают зрителя Lumos для обмена прогрессом. */
export const jellyfinUserSchema = z.object({
    id: z.string(),
    name: z.string(),
});
/**
 * Кого предлагать в привязке. Пустой список — обычное дело: медиатека может
 * быть выключена или недоступна, и тогда выбирать не из кого.
 */
export const jellyfinUsersResponseSchema = z.object({
    users: z.array(jellyfinUserSchema),
});
export const userSettingsListResponseSchema = z.object({
    users: z.array(userSettingsEntrySchema),
});
/**
 * Источники, известные серверу, — чтобы админка показывала список, а не просила
 * угадывать написание. Ключ — то, чем источник записан в настройке; подпись —
 * то, как он выглядит в меню у зрителя.
 *
 * Откуда список: из сборки (балансеры, Jellyfin, TorrServer), от Jackett
 * (трекеры) и от самого сказа (его витрины, включая те, у которых конкретного
 * тайтла не оказалось), плюс всё, что реально приходило в выдаче.
 */
export const sourceCatalogEntrySchema = z.object({
    key: z.string(),
    label: z.string(),
});
export const sourceCatalogResponseSchema = z.object({
    online: z.array(sourceCatalogEntrySchema),
    torrent: z.array(sourceCatalogEntrySchema),
});
export const rollbackRequestSchema = z.object({
    version: z.number().int().nonnegative().optional(),
});
/**
 * То, что реально получает клиент. Здесь нет ни ключей, ни внутренних адресов,
 * ни server-only лимитов, которые клиенту знать незачем.
 */
export const clientConfigResponseSchema = z.object({
    platform: devicePlatformSchema,
    configVersion: z.number().int().nonnegative(),
    generatedAt: z.string(),
    appVersion: z.string(),
    ui: z.object({
        brandName: z.string(),
        welcomeMessage: z.string(),
        hiddenSections: z.array(z.string()),
        railItemLimit: z.number().int().positive(),
        showRatings: z.boolean(),
    }),
    features: featureFlagsSchema,
    playback: z.object({
        allowedModes: z.array(playbackModeSchema),
        maxHeight: z.number().int().positive(),
        maxFrameRate: z.number().int().positive(),
        preferredVideoCodecs: z.array(z.string()),
        preferredAudioCodecs: z.array(z.string()),
        subtitlePolicy: z.string(),
        bufferingTargetMs: z.number().int().positive(),
        retry: z.object({ maxAttempts: z.number().int(), backoffMs: z.number().int() }),
    }),
    plugins: z.array(pluginDescriptorSchema),
});
//# sourceMappingURL=settings.js.map